import React, { useState, useEffect, useRef } from 'react';
import './Chatbot.css';
import { FaPaperPlane, FaTimes, FaRobot, FaExclamationTriangle } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const WELCOME_MESSAGE = {
  text: "👋 Hi! I'm Rahul's AI assistant. Ask me about his **experience**, **skills**, **projects**, or **education** — I'm happy to help!\n\n_Tip: Try asking \"What is Rahul currently working on?\" or \"What are his top AI skills?\"_",
  sender: 'bot',
  timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  id: 'welcome'
};

const Chatbot = ({ onClose }) => {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    void messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    if (!sessionId) {
      setSessionId(uuidv4());
    }
  }, [sessionId]);

  useEffect(() => {
    if (!isStreaming && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isStreaming]);

  const handleSend = async () => {
    if (!input.trim() || isStreaming) return;

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const userMessage = { text: input, sender: 'user', timestamp };
    setMessages(prev => [...prev, userMessage]);
    const messageToSend = input;
    setInput('');
    setIsStreaming(true);
    setIsTyping(true);

    const typingMessage = { text: '', sender: 'bot', timestamp: '', isTyping: true, id: Date.now() };
    setMessages(prev => [...prev, typingMessage]);

    try {
      const response = await fetch('/api/chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: messageToSend, session_id: sessionId }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedText = '';
      const botTimestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      const timeoutId = setTimeout(() => {
        reader.cancel();
        setMessages(prev => {
          const filtered = prev.filter(msg => !msg.isTyping);
          return [...filtered, { text: 'Sorry, the response took too long. Please try again.', sender: 'bot', timestamp: botTimestamp }];
        });
        setIsStreaming(false);
        setIsTyping(false);
      }, 30000);

      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isTyping);
        return [...filtered, { text: '', sender: 'bot', timestamp: botTimestamp, id: typingMessage.id, isStreaming: true }];
      });
      setIsTyping(false);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'start') {
                if (data.session_id && data.session_id !== sessionId) setSessionId(data.session_id);
              } else if (data.type === 'chunk') {
                streamedText += data.content;
                setMessages(prev => prev.map(msg => msg.id === typingMessage.id ? { ...msg, text: streamedText } : msg));
              } else if (data.type === 'end') {
                clearTimeout(timeoutId);
                if (data.session_id && data.session_id !== sessionId) setSessionId(data.session_id);
                setMessages(prev => prev.map(msg => msg.id === typingMessage.id ? { ...msg, isStreaming: false } : msg));
                break;
              } else if (data.type === 'error') {
                clearTimeout(timeoutId);
                setMessages(prev => prev.map(msg => msg.id === typingMessage.id ? { ...msg, text: data.content, isStreaming: false } : msg));
                break;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorTimestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isTyping);
        return [...filtered, { text: "Sorry, I couldn't connect to the server.", sender: 'bot', timestamp: errorTimestamp }];
      });
      setIsTyping(false);
    } finally {
      setIsStreaming(false);
    }
  };

  return (
    <div className={`chatbot-window ${isStreaming ? 'loading' : ''}`}>
      {/* Header */}
      <div className="chatbot-header">
        <div className="chatbot-header-info">
          <div className="chatbot-avatar">
            <FaRobot />
            <span className="status-dot"></span>
          </div>
          <div className="chatbot-header-text">
            <span className="chatbot-title">Rahul's AI Assistant</span>
            <span className="chatbot-subtitle">Ask me about Rahul ✦</span>
          </div>
        </div>
        <button onClick={onClose} className="chatbot-close-btn" aria-label="Close chat">
          <FaTimes />
        </button>
      </div>

      {/* Messages */}
      <div className="chatbot-messages">
        {messages.map((msg, index) => (
          <div key={msg.id || index} className={`message-row ${msg.sender}`}>
            {msg.sender === 'bot' && (
              <div className="message-avatar bot-avatar">
                <FaRobot />
              </div>
            )}
            <div className="message-bubble-wrap">
              <div className={`message-bubble ${msg.sender} ${msg.isStreaming ? 'streaming' : ''}`}>
                {msg.isTyping ? (
                  <div className="typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                ) : msg.sender === 'bot' ? (
                  <div className="markdown-content">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => <p className="md-p">{children}</p>,
                        strong: ({ children }) => <strong className="md-strong">{children}</strong>,
                        em: ({ children }) => <em className="md-em">{children}</em>,
                        a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="md-link">{children}</a>,
                        h1: ({ children }) => <h1 className="md-h1">{children}</h1>,
                        h2: ({ children }) => <h2 className="md-h2">{children}</h2>,
                        h3: ({ children }) => <h3 className="md-h3">{children}</h3>,
                        h4: ({ children }) => <h4 className="md-h4">{children}</h4>,
                        ul: ({ children }) => <ul className="md-ul">{children}</ul>,
                        ol: ({ children }) => <ol className="md-ol">{children}</ol>,
                        li: ({ children }) => <li className="md-li">{children}</li>,
                        blockquote: ({ children }) => <blockquote className="md-blockquote">{children}</blockquote>,
                        hr: () => <hr className="md-hr" />,
                        pre: ({ children }) => <div className="md-code-block"><pre className="md-pre">{children}</pre></div>,
                        code: ({ className, children }) => {
                          if (className) {
                            const lang = className.replace('language-', '');
                            return <>{lang && <span className="md-code-lang">{lang}</span>}<code className="md-pre-code">{children}</code></>;
                          }
                          return <code className="inline-code">{children}</code>;
                        },
                        table: ({ children }) => <div className="md-table-wrap"><table className="md-table">{children}</table></div>,
                        thead: ({ children }) => <thead className="md-thead">{children}</thead>,
                        tbody: ({ children }) => <tbody>{children}</tbody>,
                        tr: ({ children }) => <tr className="md-tr">{children}</tr>,
                        th: ({ children }) => <th className="md-th">{children}</th>,
                        td: ({ children }) => <td className="md-td">{children}</td>,
                      }}
                    >
                      {msg.text}
                    </ReactMarkdown>
                    {msg.isStreaming && <span className="cursor" />}
                  </div>
                ) : (
                  <span>{msg.text}</span>
                )}
              </div>
              {msg.timestamp && <span className="message-time">{msg.timestamp}</span>}
            </div>
            {msg.sender === 'user' && (
              <div className="message-avatar user-avatar">RB</div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chatbot-input-area">
        <div className="chatbot-input-row">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleSend()}
            placeholder={isStreaming ? 'Generating response...' : 'Ask about Rahul...'}
            disabled={isStreaming}
            maxLength={500}
          />
          <button onClick={handleSend} disabled={isStreaming || !input.trim()} className="send-btn" aria-label="Send message">
            <FaPaperPlane />
          </button>
        </div>
        <div className="chatbot-disclaimer">
          <FaExclamationTriangle className="disclaimer-icon" />
          <span>
            AI may hallucinate. For accurate info,{' '}
            <a href="mailto:rahulbhoyaroffice@gmail.com">contact Rahul</a>.
          </span>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
